import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, eur } from "@/lib/api";

const METHODS = [
  { v: "especes", l: "Espèces" }, { v: "virement", l: "Virement" },
  { v: "cheque", l: "Chèque" }, { v: "airbnb", l: "Airbnb" }, { v: "autre", l: "Autre" },
];

const EMPTY = {
  gite_slug: "", start_date: "", end_date: "", guests: 2, adults: 2,
  name: "", email: "", phone: "", total: "", payment_method: "especes", message: "",
  allow_overlap: false,
};

export default function ManualBookingForm({ gites, onCreated, onClose }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.gite_slug || !f.start_date || !f.end_date || !f.name.trim()) {
      return toast.error("Gîte, dates et nom du client sont obligatoires");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bookings/manual", {
        ...f, guests: Number(f.guests), adults: Number(f.adults),
        total: f.total === "" ? null : Number(f.total),
      });
      toast.success(`Réservation enregistrée — ${data.nights} nuit(s), ${eur(data.total)}`);
      setF(EMPTY);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-6 border border-brass/40 bg-panel p-6" data-testid="manual-booking-form">
      <h3 className="font-display text-xl text-bone">Nouvelle réservation hors ligne</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Pour un séjour réglé en espèces, par virement ou par chèque. Les dates sont bloquées
        immédiatement et le montant compte dans vos revenus. Aucun paiement en ligne n'est demandé.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Gîte *</span>
          <select className="field !py-2" value={f.gite_slug} onChange={set("gite_slug")} data-testid="manual-gite">
            <option value="">Choisir…</option>
            {gites.map((g) => <option key={g.slug} value={g.slug}>{g.name}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Arrivée *</span>
          <input type="date" className="field !py-2" value={f.start_date} onChange={set("start_date")} data-testid="manual-start" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Départ *</span>
          <input type="date" className="field !py-2" value={f.end_date} onChange={set("end_date")} data-testid="manual-end" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Moyen de paiement</span>
          <select className="field !py-2" value={f.payment_method} onChange={set("payment_method")} data-testid="manual-method">
            {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Nom du client *</span>
          <input className="field !py-2" value={f.name} onChange={set("name")} data-testid="manual-name" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Email (pour les infos d'arrivée)</span>
          <input type="email" className="field !py-2" value={f.email} onChange={set("email")} data-testid="manual-email" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Téléphone</span>
          <input className="field !py-2" value={f.phone} onChange={set("phone")} data-testid="manual-phone" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Montant total (€) — vide = tarif du calendrier</span>
          <input type="number" min="0" step="0.01" className="field !py-2" value={f.total} onChange={set("total")} data-testid="manual-total" />
        </label>

        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Voyageurs</span>
          <input type="number" min="1" className="field !py-2" value={f.guests} onChange={set("guests")} data-testid="manual-guests" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Dont majeurs</span>
          <input type="number" min="0" className="field !py-2" value={f.adults} onChange={set("adults")} data-testid="manual-adults" />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="mb-1.5 block text-xs text-bone">Note interne</span>
          <input className="field !py-2" value={f.message} onChange={set("message")} data-testid="manual-message" />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-bone">
          <input type="checkbox" checked={f.allow_overlap} data-testid="manual-force"
            onChange={(e) => setF({ ...f, allow_overlap: e.target.checked })} />
          Forcer même si les dates sont déjà bloquées (séjour venant d'Airbnb)
        </label>
      </div>

      <div className="mt-5 flex gap-4">
        <button onClick={submit} disabled={busy} className="btn-primary" data-testid="manual-submit">
          {busy ? "Enregistrement…" : "Enregistrer la réservation"}
        </button>
        <button onClick={onClose} className="btn-ghost" data-testid="manual-cancel">Fermer</button>
      </div>
    </div>
  );
}
